import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "d-flex justify-space-between align-center mb-6" }
const _hoisted_2 = { class: "d-flex align-center" }
const _hoisted_3 = { class: "text-h4 font-weight-bold" }
const _hoisted_4 = { class: "d-flex align-center" }
const _hoisted_5 = { class: "text-h4 font-weight-bold" }
const _hoisted_6 = { class: "text-caption text-info" }
const _hoisted_7 = { class: "d-flex align-center" }
const _hoisted_8 = { class: "text-h4 font-weight-bold" }
const _hoisted_9 = { class: "d-flex align-center" }
const _hoisted_10 = { class: "text-h4 font-weight-bold" }
const _hoisted_11 = { class: "text-h6 font-weight-bold" }
const _hoisted_12 = {
  key: 0,
  class: "text-center pa-8"
}
const _hoisted_13 = {
  key: 1,
  class: "text-center pa-8"
}
const _hoisted_14 = { class: "px-6" }
const _hoisted_15 = { class: "d-flex align-center" }
const _hoisted_16 = { class: "font-family-monospace font-weight-bold" }
const _hoisted_17 = { class: "font-weight-medium" }
const _hoisted_18 = { class: "text-caption text-medium-emphasis" }
const _hoisted_19 = { class: "font-weight-medium" }
const _hoisted_20 = { class: "text-caption text-medium-emphasis" }
const _hoisted_21 = { class: "d-flex align-center" }
const _hoisted_22 = { class: "text-body-2" }
const _hoisted_23 = { class: "text-caption text-medium-emphasis" }
const _hoisted_24 = { class: "d-flex gap-1" }
const _hoisted_25 = {
  key: 3,
  class: "d-flex justify-center align-center pa-4"
}
const _hoisted_26 = { class: "text-body-1" }
const _hoisted_27 = { class: "mt-4 pa-3 bg-grey-lighten-5 rounded" }
const _hoisted_28 = { class: "text-body-2" }
const _hoisted_29 = { class: "text-body-2" }
const _hoisted_30 = { class: "text-body-2" }

import { ref, computed, onMounted } from 'vue'
import { useDeviceStore } from '@/views/Devices/store'
import { useUserStore } from '@/views/Companies/store'
import { useLocationsStore } from '@/views/Locations/store'
import { Device } from '@/views/Devices/models/device'
import { Company } from '@/views/Companies/models/company'
import { Locations } from '@/views/Locations/models/locations'

// Stores

export default /*@__PURE__*/_defineComponent({
  __name: 'ManagerDevicesView',
  setup(__props) {

const deviceStore = useDeviceStore()
const userStore = useUserStore()
const locationsStore = useLocationsStore()

// Local state
const showCreateDialog = ref(false)
const showEditDialog = ref(false)
const showDeleteDialog = ref(false)
const selectedDevice = ref<Device | null>(null)
const searchQuery = ref('')
const filterCompany = ref('all')
const filterStatus = ref('all')
const itemsPerPage = ref(10)
const currentPage = ref(1)
const loading = ref(false)
const error = ref<string | null>(null)

// Form data
const deviceForm = ref({
  code: '',
  name: '',
  extendedName: '',
  description: '',
  companyId: '',
  locationId: '',
  active: true,
  lat: 0,
  lng: 0,
  altitude: 0,
  periodicity: 30,
  groupId: 1
})
// Helper methods
const getCompanyByLocationId = (locationId: string) => {
  const location = locations.value.find(l => l.id === locationId)
  return location ? companies.value.find(c => c.id === location.companyId.toString()) : null
}

// Get real data from stores
const companies = computed(() => userStore.companies)
const locations = computed(() => locationsStore.locations)

// Get devices from store instead of sample data
const devices = computed(() => deviceStore.devices)

// Dashboard stats computed from real data
const dashboardStats = computed(() => ({
  totalDevices: devices.value.length,
  activeDevices: devices.value.filter(d => d.active).length,
  alarmedDevices: devices.value.filter(d => d.alarmed).length,
  companiesWithDevices: new Set(devices.value.map(d => getCompanyByLocationId(d.locationId)?.id)).size
}))

// Computed properties
const filteredDevices = computed(() => {
  let filtered = devices.value

  // Filter by company
  if (filterCompany.value !== 'all') {
    filtered = filtered.filter(device => {
      const location = locations.value.find(l => l.id === device.locationId)
      return location?.companyId.toString() === filterCompany.value
    })
  }

  // Filter by status
  if (filterStatus.value === 'active') {
    filtered = filtered.filter(d => d.active)
  } else if (filterStatus.value === 'inactive') {
    filtered = filtered.filter(d => !d.active)
  } else if (filterStatus.value === 'alarmed') {
    filtered = filtered.filter(d => d.alarmed)
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(device => 
      device.name.toLowerCase().includes(query) ||
      device.id.toLowerCase().includes(query) ||
      device.description.toLowerCase().includes(query)
    )
  }

  return filtered
})

const paginatedDevices = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredDevices.value.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(filteredDevices.value.length / itemsPerPage.value)
})

const availableLocations = computed(() => {
  if (deviceForm.value.companyId) {
    return locations.value.filter(l => l.companyId.toString() === deviceForm.value.companyId)
  }
  return locations.value
})



const getLocationById = (locationId: string) => {
  return locations.value.find(l => l.id === locationId)
}

const getStatusColor = (device: Device) => {
  if (!device.active) return 'error'
  if (device.alarmed) return 'warning'
  return 'success'
}

const getStatusText = (device: Device) => {
  if (!device.active) return 'Inactivo'
  if (device.alarmed) return 'Con Alarma'
  return 'Activo'
}

const getStatusIcon = (device: Device) => {
  if (!device.active) return 'mdi-power-off'
  if (device.alarmed) return 'mdi-alert-circle'  
  return 'mdi-check-circle'
}

// Dialog methods
const openCreateDialog = () => {
  resetForm()
  showCreateDialog.value = true
}

const openEditDialog = (device: Device) => {
  selectedDevice.value = device
  deviceForm.value = {
    code: device.code,
    name: device.name,
    extendedName: device.extendedName,
    description: device.description,
    companyId: getCompanyByLocationId(device.locationId)?.id || '',
    locationId: device.locationId,
    active: device.active,
    lat: device.lat,
    lng: device.lng,
    altitude: device.altitude,
    periodicity: device.periodicity,
    groupId: device.groupId
  }
  showEditDialog.value = true
}

const openDeleteDialog = (device: Device) => {
  selectedDevice.value = device
  showDeleteDialog.value = true
}

const closeDialogs = () => {
  showCreateDialog.value = false
  showEditDialog.value = false
  showDeleteDialog.value = false
  selectedDevice.value = null
  resetForm()
}

const resetForm = () => {
  deviceForm.value = {
    code: '',
    name: '',
    extendedName: '',
    description: '',
    companyId: '',
    locationId: '',
    active: true,
    lat: 0,
    lng: 0,
    altitude: 0,
    periodicity: 30,
    groupId: 1
  }
}

// CRUD methods
const createDevice = async () => {
  // Validación básica
  if (!deviceForm.value.code || !deviceForm.value.name || !deviceForm.value.companyId || !deviceForm.value.locationId) {
    error.value = 'Por favor complete todos los campos requeridos'
    return
  }

  loading.value = true
  error.value = null
  
  try {
    // Generar slug desde el nombre
    const slug = deviceForm.value.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    
    const newDevice = new Device({
      ...deviceForm.value,
      slug: slug,
      initDate: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'admin', // TODO: Get from auth store
      updatedBy: 'admin',
      alarmed: false,
      metadata: '{}'
    })
    
    console.log('Creating device:', newDevice.toCreateObject())
    await deviceStore.postDevice(newDevice)
    console.log('Device created successfully')
    await loadAllData() // Recargar todos los datos
    closeDialogs()
  } catch (err) {
    console.error('Error creating device:', err)
    error.value = err instanceof Error ? err.message : 'Error al crear dispositivo'
  } finally {
    loading.value = false
  }
}

const updateDevice = async () => {
  if (!selectedDevice.value) return

  // Validación básica
  if (!deviceForm.value.code || !deviceForm.value.name || !deviceForm.value.companyId || !deviceForm.value.locationId) {
    error.value = 'Por favor complete todos los campos requeridos'
    return
  }

  loading.value = true
  error.value = null
  
  try {
    // Generar slug desde el nombre
    const slug = deviceForm.value.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    
    const updatedDevice = new Device({
      ...selectedDevice.value.toPlainObject(),
      ...deviceForm.value,
      slug: slug,
      updatedAt: new Date(),
      updatedBy: 'admin' // TODO: Get from auth store
    })
    
    console.log('Updating device:', updatedDevice)
    await deviceStore.putDevice(updatedDevice)
    console.log('Device updated successfully')
    await loadAllData() // Recargar todos los datos
    closeDialogs()
  } catch (err) {
    console.error('Error updating device:', err)
    error.value = err instanceof Error ? err.message : 'Error al actualizar dispositivo'
  } finally {
    loading.value = false
  }
}

const deleteDevice = async () => {
  if (!selectedDevice.value) return

  loading.value = true
  error.value = null
  
  try {
    await deviceStore.deleteDevice(selectedDevice.value.id)
    await loadAllData() // Recargar todos los datos
    closeDialogs()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Error al eliminar dispositivo'
    console.error('Error al eliminar dispositivo:', err)
  } finally {
    loading.value = false
  }
}

// Load devices from API
const loadDevices = async () => {
  loading.value = true
  error.value = null
  
  try {
    await deviceStore.getDevices()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Error al cargar dispositivos'
    console.error('Error al cargar dispositivos:', err)
  } finally {
    loading.value = false
  }
}

// Load companies from API
const loadCompanies = async () => {
  try {
    await userStore.getCompanies()
  } catch (err) {
    console.error('Error al cargar compañías:', err)
  }
}

// Load locations from API
const loadLocations = async () => {
  try {
    await locationsStore.getLocations()
  } catch (err) {
    console.error('Error al cargar ubicaciones:', err)
  }
}

// Load all data
const loadAllData = async () => {
  await Promise.all([
    loadDevices(),
    loadCompanies(),
    loadLocations()
  ])
}

const formatDate = (date: Date) => {
  // return date.toLocaleDateString('es-ES', {
  //   year: 'numeric',
  //   month: '2-digit',
  //   day: '2-digit'
  // })
  return date
}

// Lifecycle
onMounted(async () => {
  await loadAllData()
})

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_alert_title = _resolveComponent("v-alert-title")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_pagination = _resolveComponent("v-pagination")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, { fluid: "" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _cache[33] || (_cache[33] = _createElementVNode("div", null, [
                  _createElementVNode("h1", { class: "text-h4 font-weight-bold" }, "Gestión de Dispositivos"),
                  _createElementVNode("p", { class: "text-body-1 text-medium-emphasis" }, "Administración de Fotorizotron y asignación a compañías")
                ], -1)),
                _createElementVNode("div", null, [
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    "prepend-icon": "mdi-plus",
                    onClick: openCreateDialog
                  }, {
                    default: _withCtx(() => _cache[32] || (_cache[32] = [
                      _createTextVNode(" Nuevo Dispositivo ")
                    ])),
                    _: 1
                  })
                ])
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      (loading.value)
        ? (_openBlock(), _createBlock(_component_v_row, {
            key: 0,
            class: "mb-6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "12",
                class: "text-center"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_progress_circular, {
                    indeterminate: "",
                    color: "primary",
                    size: "64"
                  }),
                  _cache[34] || (_cache[34] = _createElementVNode("p", { class: "mt-4 text-body-1" }, "Cargando dispositivos...", -1))
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : (error.value)
          ? (_openBlock(), _createBlock(_component_v_row, {
              key: 1,
              class: "mb-6"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, { cols: "12" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_alert, {
                      type: "error",
                      prominent: ""
                    }, {
                      append: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          color: "white",
                          variant: "text",
                          onClick: loadDevices
                        }, {
                          default: _withCtx(() => _cache[36] || (_cache[36] = [
                            _createTextVNode(" Reintentar ")
                          ])),
                          _: 1
                        })
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_component_v_alert_title, null, {
                          default: _withCtx(() => _cache[35] || (_cache[35] = [
                            _createTextVNode("Error al cargar dispositivos")
                          ])),
                          _: 1
                        }),
                        _createTextVNode(" " + _toDisplayString(error.value) + " ", 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : (_openBlock(), _createBlock(_component_v_row, {
              key: 2,
              class: "mb-6"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, {
                  cols: "12",
                  sm: "6",
                  md: "3"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card, {
                      flat: "",
                      elevation: "2",
                      class: "rounded-lg"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card_text, { class: "pb-0" }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_2, [
                              _createVNode(_component_v_avatar, {
                                color: "#E8F5E8",
                                size: "48",
                                class: "mr-3"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, {
                                    color: "#4CAF50",
                                    size: "24"
                                  }, {
                                    default: _withCtx(() => _cache[37] || (_cache[37] = [
                                      _createTextVNode("mdi-camera-outline")
                                    ])),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createElementVNode("div", null, [
                                _cache[38] || (_cache[38] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Total Dispositivos", -1)),
                                _createElementVNode("div", _hoisted_3, _toDisplayString(dashboardStats.value.totalDevices), 1),
                                _cache[39] || (_cache[39] = _createElementVNode("div", { class: "text-caption text-success" }, "En el sistema", -1))
                              ])
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_card_text, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_progress_linear, {
                              "model-value": "100",
                              height: "6",
                              color: "#4CAF50",
                              rounded: ""
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, {
                  cols: "12",
                  sm: "6",
                  md: "3"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card, {
                      flat: "",
                      elevation: "2",
                      class: "rounded-lg"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card_text, { class: "pb-0" }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_4, [
                              _createVNode(_component_v_avatar, {
                                color: "#E3F2FD",
                                size: "48",
                                class: "mr-3"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, {
                                    color: "#2196F3",
                                    size: "24"
                                  }, {
                                    default: _withCtx(() => _cache[40] || (_cache[40] = [
                                      _createTextVNode("mdi-check-circle")
                                    ])),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createElementVNode("div", null, [
                                _cache[41] || (_cache[41] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Dispositivos Activos", -1)),
                                _createElementVNode("div", _hoisted_5, _toDisplayString(dashboardStats.value.activeDevices), 1),
                                _createElementVNode("div", _hoisted_6, _toDisplayString(Math.round((dashboardStats.value.activeDevices/dashboardStats.value.totalDevices)*100)) + "% operativos", 1)
                              ])
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_card_text, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_progress_linear, {
                              "model-value": (dashboardStats.value.activeDevices/dashboardStats.value.totalDevices)*100,
                              height: "6",
                              color: "#2196F3",
                              rounded: ""
                            }, null, 8, ["model-value"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, {
                  cols: "12",
                  sm: "6",
                  md: "3"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card, {
                      flat: "",
                      elevation: "2",
                      class: "rounded-lg"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card_text, { class: "pb-0" }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_7, [
                              _createVNode(_component_v_avatar, {
                                color: "#FFF3E0",
                                size: "48",
                                class: "mr-3"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, {
                                    color: "#FF9800",
                                    size: "24"
                                  }, {
                                    default: _withCtx(() => _cache[42] || (_cache[42] = [
                                      _createTextVNode("mdi-alert-circle")
                                    ])),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createElementVNode("div", null, [
                                _cache[43] || (_cache[43] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Con Alarmas", -1)),
                                _createElementVNode("div", _hoisted_8, _toDisplayString(dashboardStats.value.alarmedDevices), 1),
                                _createElementVNode("div", {
                                  class: _normalizeClass(["text-caption", dashboardStats.value.alarmedDevices > 0 ? 'text-warning' : 'text-success'])
                                }, _toDisplayString(dashboardStats.value.alarmedDevices > 0 ? 'Requieren atención' : 'Todo normal'), 3)
                              ])
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_card_text, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_progress_linear, {
                              "model-value": dashboardStats.value.alarmedDevices > 0 ? (dashboardStats.value.alarmedDevices/dashboardStats.value.totalDevices)*100 : 5,
                              height: "6",
                              color: "#FF9800",
                              rounded: ""
                            }, null, 8, ["model-value"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, {
                  cols: "12",
                  sm: "6",
                  md: "3"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card, {
                      flat: "",
                      elevation: "2",
                      class: "rounded-lg"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card_text, { class: "pb-0" }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_9, [
                              _createVNode(_component_v_avatar, {
                                color: "#F3E5F5",
                                size: "48",
                                class: "mr-3"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, {
                                    color: "#9C27B0",
                                    size: "24"
                                  }, {
                                    default: _withCtx(() => _cache[44] || (_cache[44] = [
                                      _createTextVNode("mdi-domain")
                                    ])),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createElementVNode("div", null, [
                                _cache[45] || (_cache[45] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Compañías Activas", -1)),
                                _createElementVNode("div", _hoisted_10, _toDisplayString(dashboardStats.value.companiesWithDevices), 1),
                                _cache[46] || (_cache[46] = _createElementVNode("div", { class: "text-caption text-purple" }, "Con dispositivos asignados", -1))
                              ])
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_card_text, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_progress_linear, {
                              "model-value": (dashboardStats.value.companiesWithDevices/companies.value.length)*100,
                              height: "6",
                              color: "#9C27B0",
                              rounded: ""
                            }, null, 8, ["model-value"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })),
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                flat: "",
                elevation: "2",
                class: "rounded-lg"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_row, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "3"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: searchQuery.value,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                                label: "Buscar dispositivos",
                                "prepend-inner-icon": "mdi-magnify",
                                variant: "outlined",
                                density: "comfortable",
                                "hide-details": "",
                                clearable: ""
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "3"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: filterCompany.value,
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filterCompany).value = $event)),
                                items: [
                    { title: 'Todas las compañías', value: 'all' },
                    ...companies.value.map(c => ({ title: c.name, value: c.id }))
                  ],
                                label: "Compañía",
                                variant: "outlined",
                                density: "comfortable",
                                "hide-details": ""
                              }, null, 8, ["modelValue", "items"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "3"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: filterStatus.value,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filterStatus).value = $event)),
                                items: [
                    { title: 'Todos los estados', value: 'all' },
                    { title: 'Activos', value: 'active' },
                    { title: 'Inactivos', value: 'inactive' },
                    { title: 'Con alarmas', value: 'alarmed' }
                  ],
                                label: "Estado",
                                variant: "outlined",
                                density: "comfortable",
                                "hide-details": ""
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "3"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: itemsPerPage.value,
                                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((itemsPerPage).value = $event)),
                                items: [5, 10, 25, 50],
                                label: "Elementos por página",
                                variant: "outlined",
                                density: "comfortable",
                                "hide-details": ""
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, { class: "mt-6" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                flat: "",
                elevation: "2",
                class: "rounded-lg"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-6 py-4" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_11, [
                        _cache[47] || (_cache[47] = _createTextVNode(" Dispositivos Fotorizotron ")),
                        _createVNode(_component_v_chip, {
                          size: "small",
                          color: "primary",
                          class: "ml-2"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(filteredDevices.value.length), 1)
                          ]),
                          _: 1
                        })
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, { class: "px-0" }, {
                    default: _withCtx(() => [
                      (loading.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                            _createVNode(_component_v_progress_circular, {
                              indeterminate: "",
                              color: "primary",
                              size: "40"
                            }),
                            _cache[48] || (_cache[48] = _createElementVNode("p", { class: "mt-4 text-body-1" }, "Cargando dispositivos...", -1))
                          ]))
                        : (devices.value.length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                              _createVNode(_component_v_icon, {
                                size: "64",
                                color: "grey-lighten-1"
                              }, {
                                default: _withCtx(() => _cache[49] || (_cache[49] = [
                                  _createTextVNode("mdi-camera-outline")
                                ])),
                                _: 1
                              }),
                              _cache[50] || (_cache[50] = _createElementVNode("p", { class: "mt-4 text-h6 text-grey" }, "No hay dispositivos registrados", -1)),
                              _cache[51] || (_cache[51] = _createElementVNode("p", { class: "text-body-2 text-grey" }, "Los dispositivos aparecerán aquí una vez que se registren en el sistema.", -1))
                            ]))
                          : (_openBlock(), _createBlock(_component_v_table, { key: 2 }, {
                              default: _withCtx(() => [
                                _cache[57] || (_cache[57] = _createElementVNode("thead", null, [
                                  _createElementVNode("tr", null, [
                                    _createElementVNode("th", { class: "text-left px-6" }, "ID"),
                                    _createElementVNode("th", { class: "text-left" }, "Nombre"),
                                    _createElementVNode("th", { class: "text-left" }, "Compañía"),
                                    _createElementVNode("th", { class: "text-left" }, "Ubicación"),
                                    _createElementVNode("th", { class: "text-left" }, "Estado"),
                                    _createElementVNode("th", { class: "text-left" }, "Periodicidad"),
                                    _createElementVNode("th", { class: "text-left" }, "Creado"),
                                    _createElementVNode("th", { class: "text-left" }, "Acciones")
                                  ])
                                ], -1)),
                                _createElementVNode("tbody", null, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedDevices.value, (device) => {
                                    return (_openBlock(), _createElementBlock("tr", {
                                      key: device.id,
                                      class: _normalizeClass(["device-row", { 'alarmed-device': device.alarmed }])
                                    }, [
                                      _createElementVNode("td", _hoisted_14, [
                                        _createElementVNode("div", _hoisted_15, [
                                          _createVNode(_component_v_icon, {
                                            icon: getStatusIcon(device),
                                            color: getStatusColor(device),
                                            size: "16",
                                            class: "mr-2"
                                          }, null, 8, ["icon", "color"]),
                                          _createElementVNode("span", _hoisted_16, _toDisplayString(device.id), 1)
                                        ])
                                      ]),
                                      _createElementVNode("td", null, [
                                        _createElementVNode("div", null, [
                                          _createElementVNode("div", _hoisted_17, _toDisplayString(device.name), 1),
                                          _createElementVNode("div", _hoisted_18, _toDisplayString(device.extendedName), 1)
                                        ])
                                      ]),
                                      _createElementVNode("td", null, [
                                        _createElementVNode("div", _hoisted_19, _toDisplayString(getCompanyByLocationId(device.locationId)?.name || 'No asignada'), 1)
                                      ]),
                                      _createElementVNode("td", null, [
                                        _createElementVNode("div", null, _toDisplayString(getLocationById(device.locationId)?.name || 'No especificada'), 1),
                                        _createElementVNode("div", _hoisted_20, _toDisplayString(device.lat.toFixed(4)) + ", " + _toDisplayString(device.lng.toFixed(4)), 1)
                                      ]),
                                      _createElementVNode("td", null, [
                                        _createVNode(_component_v_chip, {
                                          color: getStatusColor(device),
                                          size: "small",
                                          "text-color": "white"
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(getStatusText(device)), 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["color"])
                                      ]),
                                      _createElementVNode("td", null, [
                                        _createElementVNode("div", _hoisted_21, [
                                          _createVNode(_component_v_icon, {
                                            size: "16",
                                            class: "mr-1"
                                          }, {
                                            default: _withCtx(() => _cache[52] || (_cache[52] = [
                                              _createTextVNode("mdi-clock-outline")
                                            ])),
                                            _: 1
                                          }),
                                          _createElementVNode("span", null, _toDisplayString(device.periodicity) + "min", 1)
                                        ])
                                      ]),
                                      _createElementVNode("td", null, [
                                        _createElementVNode("div", _hoisted_22, _toDisplayString(formatDate(device.createdAt)), 1),
                                        _createElementVNode("div", _hoisted_23, _toDisplayString(device.createdBy), 1)
                                      ]),
                                      _createElementVNode("td", null, [
                                        _createElementVNode("div", _hoisted_24, [
                                          _createVNode(_component_v_btn, {
                                            icon: "",
                                            size: "small",
                                            color: "primary",
                                            variant: "text",
                                            onClick: ($event: any) => (openEditDialog(device))
                                          }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_icon, { size: "18" }, {
                                                default: _withCtx(() => _cache[53] || (_cache[53] = [
                                                  _createTextVNode("mdi-pencil")
                                                ])),
                                                _: 1
                                              }),
                                              _createVNode(_component_v_tooltip, {
                                                activator: "parent",
                                                location: "top"
                                              }, {
                                                default: _withCtx(() => _cache[54] || (_cache[54] = [
                                                  _createTextVNode("Editar dispositivo")
                                                ])),
                                                _: 1
                                              })
                                            ]),
                                            _: 2
                                          }, 1032, ["onClick"]),
                                          _createVNode(_component_v_btn, {
                                            icon: "",
                                            size: "small",
                                            color: "error",
                                            variant: "text",
                                            onClick: ($event: any) => (openDeleteDialog(device))
                                          }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_icon, { size: "18" }, {
                                                default: _withCtx(() => _cache[55] || (_cache[55] = [
                                                  _createTextVNode("mdi-delete")
                                                ])),
                                                _: 1
                                              }),
                                              _createVNode(_component_v_tooltip, {
                                                activator: "parent",
                                                location: "top"
                                              }, {
                                                default: _withCtx(() => _cache[56] || (_cache[56] = [
                                                  _createTextVNode("Eliminar dispositivo")
                                                ])),
                                                _: 1
                                              })
                                            ]),
                                            _: 2
                                          }, 1032, ["onClick"])
                                        ])
                                      ])
                                    ], 2))
                                  }), 128))
                                ])
                              ]),
                              _: 1
                            })),
                      (totalPages.value > 1)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                            _createVNode(_component_v_pagination, {
                              modelValue: currentPage.value,
                              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((currentPage).value = $event)),
                              length: totalPages.value,
                              "total-visible": 7,
                              color: "primary"
                            }, null, 8, ["modelValue", "length"])
                          ]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: showCreateDialog.value,
        "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((showCreateDialog).value = $event)),
        "max-width": "900",
        persistent: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h5 px-6 py-4 bg-primary text-white" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { class: "mr-2" }, {
                    default: _withCtx(() => _cache[58] || (_cache[58] = [
                      _createTextVNode("mdi-plus-circle")
                    ])),
                    _: 1
                  }),
                  _cache[59] || (_cache[59] = _createTextVNode(" Crear Nuevo Dispositivo Fotorizotron "))
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "pa-6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_form, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_row, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: deviceForm.value.code,
                                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((deviceForm.value.code) = $event)),
                                label: "Código del Dispositivo",
                                variant: "outlined",
                                density: "comfortable",
                                rules: [v => !!v || 'Campo requerido'],
                                placeholder: "ej: FT-001"
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: deviceForm.value.name,
                                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((deviceForm.value.name) = $event)),
                                label: "Nombre del Dispositivo",
                                variant: "outlined",
                                density: "comfortable",
                                rules: [v => !!v || 'Campo requerido'],
                                placeholder: "ej: Fotorizotron Norte"
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: deviceForm.value.extendedName,
                                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((deviceForm.value.extendedName) = $event)),
                                label: "Nombre Extendido",
                                variant: "outlined",
                                density: "comfortable",
                                placeholder: "ej: Fotorizotron - Análisis Radicular Norte"
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, { cols: "12" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_textarea, {
                                modelValue: deviceForm.value.description,
                                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((deviceForm.value.description) = $event)),
                                label: "Descripción",
                                variant: "outlined",
                                rows: "3",
                                placeholder: "Describe las características y propósito del dispositivo..."
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: deviceForm.value.companyId,
                                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((deviceForm.value.companyId) = $event)),
                                items: companies.value.map(c => ({ title: c.name, value: c.id })),
                                label: "Compañía",
                                variant: "outlined",
                                density: "comfortable",
                                rules: [v => !!v || 'Campo requerido']
                              }, null, 8, ["modelValue", "items", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: deviceForm.value.locationId,
                                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((deviceForm.value.locationId) = $event)),
                                items: availableLocations.value.map(l => ({ title: l.name, value: l.id })),
                                label: "Ubicación",
                                variant: "outlined",
                                density: "comfortable",
                                rules: [v => !!v || 'Campo requerido'],
                                disabled: !deviceForm.value.companyId
                              }, null, 8, ["modelValue", "items", "rules", "disabled"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: deviceForm.value.lat,
                                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((deviceForm.value.lat) = $event)),
                                modelModifiers: { number: true },
                                label: "Latitud",
                                type: "number",
                                step: "0.000001",
                                variant: "outlined",
                                density: "comfortable",
                                rules: [v => v >= -90 && v <= 90 || 'Debe estar entre -90 y 90']
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: deviceForm.value.lng,
                                "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((deviceForm.value.lng) = $event)),
                                modelModifiers: { number: true },
                                label: "Longitud",
                                type: "number",
                                step: "0.000001",
                                variant: "outlined",
                                density: "comfortable",
                                rules: [v => v >= -180 && v <= 180 || 'Debe estar entre -180 y 180']
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: deviceForm.value.altitude,
                                "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((deviceForm.value.altitude) = $event)),
                                modelModifiers: { number: true },
                                label: "Altitud (msnm)",
                                type: "number",
                                variant: "outlined",
                                density: "comfortable",
                                suffix: "metros"
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: deviceForm.value.periodicity,
                                "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((deviceForm.value.periodicity) = $event)),
                                modelModifiers: { number: true },
                                label: "Periodicidad (minutos)",
                                type: "number",
                                min: "1",
                                variant: "outlined",
                                density: "comfortable",
                                suffix: "min",
                                rules: [v => v > 0 || 'Debe ser mayor a 0']
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: deviceForm.value.groupId,
                                "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((deviceForm.value.groupId) = $event)),
                                modelModifiers: { number: true },
                                label: "ID de Grupo",
                                type: "number",
                                min: "1",
                                variant: "outlined",
                                density: "comfortable",
                                rules: [v => v > 0 || 'Debe ser mayor a 0']
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, { cols: "12" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_checkbox, {
                                modelValue: deviceForm.value.active,
                                "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((deviceForm.value.active) = $event)),
                                label: "Dispositivo activo al crear",
                                color: "success"
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "px-6 pb-4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    onClick: closeDialogs
                  }, {
                    default: _withCtx(() => _cache[60] || (_cache[60] = [
                      _createTextVNode(" Cancelar ")
                    ])),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    onClick: createDevice
                  }, {
                    default: _withCtx(() => _cache[61] || (_cache[61] = [
                      _createTextVNode(" Crear Dispositivo ")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: showEditDialog.value,
        "onUpdate:modelValue": _cache[30] || (_cache[30] = ($event: any) => ((showEditDialog).value = $event)),
        "max-width": "900",
        persistent: ""
      }, {
        default: _withCtx(() => [
          (selectedDevice.value)
            ? (_openBlock(), _createBlock(_component_v_card, { key: 0 }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "text-h5 px-6 py-4 bg-info text-white" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { class: "mr-2" }, {
                        default: _withCtx(() => _cache[62] || (_cache[62] = [
                          _createTextVNode("mdi-pencil")
                        ])),
                        _: 1
                      }),
                      _createTextVNode(" Editar Dispositivo: " + _toDisplayString(selectedDevice.value.name), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, { class: "pa-6" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_form, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_row, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: deviceForm.value.code,
                                    "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((deviceForm.value.code) = $event)),
                                    label: "Código del Dispositivo",
                                    variant: "outlined",
                                    density: "comfortable",
                                    rules: [v => !!v || 'Campo requerido'],
                                    placeholder: "ej: FT-001"
                                  }, null, 8, ["modelValue", "rules"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: deviceForm.value.name,
                                    "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((deviceForm.value.name) = $event)),
                                    label: "Nombre del Dispositivo",
                                    variant: "outlined",
                                    density: "comfortable",
                                    rules: [v => !!v || 'Campo requerido']
                                  }, null, 8, ["modelValue", "rules"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: deviceForm.value.extendedName,
                                    "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((deviceForm.value.extendedName) = $event)),
                                    label: "Nombre Extendido",
                                    variant: "outlined",
                                    density: "comfortable"
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, { cols: "12" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_textarea, {
                                    modelValue: deviceForm.value.description,
                                    "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((deviceForm.value.description) = $event)),
                                    label: "Descripción",
                                    variant: "outlined",
                                    rows: "3"
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_select, {
                                    modelValue: deviceForm.value.companyId,
                                    "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((deviceForm.value.companyId) = $event)),
                                    items: companies.value.map(c => ({ title: c.name, value: c.id })),
                                    label: "Compañía",
                                    variant: "outlined",
                                    density: "comfortable",
                                    rules: [v => !!v || 'Campo requerido']
                                  }, null, 8, ["modelValue", "items", "rules"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_select, {
                                    modelValue: deviceForm.value.locationId,
                                    "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((deviceForm.value.locationId) = $event)),
                                    items: availableLocations.value.map(l => ({ title: l.name, value: l.id })),
                                    label: "Ubicación",
                                    variant: "outlined",
                                    density: "comfortable",
                                    rules: [v => !!v || 'Campo requerido']
                                  }, null, 8, ["modelValue", "items", "rules"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "4"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: deviceForm.value.lat,
                                    "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((deviceForm.value.lat) = $event)),
                                    modelModifiers: { number: true },
                                    label: "Latitud",
                                    type: "number",
                                    step: "0.000001",
                                    variant: "outlined",
                                    density: "comfortable"
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "4"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: deviceForm.value.lng,
                                    "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event: any) => ((deviceForm.value.lng) = $event)),
                                    modelModifiers: { number: true },
                                    label: "Longitud",
                                    type: "number",
                                    step: "0.000001",
                                    variant: "outlined",
                                    density: "comfortable"
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "4"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: deviceForm.value.altitude,
                                    "onUpdate:modelValue": _cache[26] || (_cache[26] = ($event: any) => ((deviceForm.value.altitude) = $event)),
                                    modelModifiers: { number: true },
                                    label: "Altitud (msnm)",
                                    type: "number",
                                    variant: "outlined",
                                    density: "comfortable",
                                    suffix: "metros"
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: deviceForm.value.periodicity,
                                    "onUpdate:modelValue": _cache[27] || (_cache[27] = ($event: any) => ((deviceForm.value.periodicity) = $event)),
                                    modelModifiers: { number: true },
                                    label: "Periodicidad (minutos)",
                                    type: "number",
                                    min: "1",
                                    variant: "outlined",
                                    density: "comfortable",
                                    suffix: "min"
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: deviceForm.value.groupId,
                                    "onUpdate:modelValue": _cache[28] || (_cache[28] = ($event: any) => ((deviceForm.value.groupId) = $event)),
                                    modelModifiers: { number: true },
                                    label: "ID de Grupo",
                                    type: "number",
                                    min: "1",
                                    variant: "outlined",
                                    density: "comfortable"
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, { cols: "12" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_checkbox, {
                                    modelValue: deviceForm.value.active,
                                    "onUpdate:modelValue": _cache[29] || (_cache[29] = ($event: any) => ((deviceForm.value.active) = $event)),
                                    label: "Dispositivo activo",
                                    color: "success"
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_actions, { class: "px-6 pb-4" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_spacer),
                      _createVNode(_component_v_btn, {
                        variant: "text",
                        onClick: closeDialogs
                      }, {
                        default: _withCtx(() => _cache[63] || (_cache[63] = [
                          _createTextVNode(" Cancelar ")
                        ])),
                        _: 1
                      }),
                      _createVNode(_component_v_btn, {
                        color: "primary",
                        onClick: updateDevice
                      }, {
                        default: _withCtx(() => _cache[64] || (_cache[64] = [
                          _createTextVNode(" Guardar Cambios ")
                        ])),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: showDeleteDialog.value,
        "onUpdate:modelValue": _cache[31] || (_cache[31] = ($event: any) => ((showDeleteDialog).value = $event)),
        "max-width": "500"
      }, {
        default: _withCtx(() => [
          (selectedDevice.value)
            ? (_openBlock(), _createBlock(_component_v_card, { key: 0 }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "text-h5 px-6 py-4 bg-error text-white" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { class: "mr-2" }, {
                        default: _withCtx(() => _cache[65] || (_cache[65] = [
                          _createTextVNode("mdi-delete")
                        ])),
                        _: 1
                      }),
                      _cache[66] || (_cache[66] = _createTextVNode(" Confirmar Eliminación "))
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, { class: "pa-6" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_alert, {
                        type: "warning",
                        variant: "tonal",
                        class: "mb-4"
                      }, {
                        default: _withCtx(() => _cache[67] || (_cache[67] = [
                          _createTextVNode(" Esta acción no se puede deshacer. Se eliminará permanentemente el dispositivo y todos sus datos asociados. ")
                        ])),
                        _: 1
                      }),
                      _createElementVNode("p", _hoisted_26, [
                        _cache[68] || (_cache[68] = _createTextVNode(" ¿Estás seguro de que deseas eliminar el dispositivo ")),
                        _createElementVNode("strong", null, _toDisplayString(selectedDevice.value.name), 1),
                        _cache[69] || (_cache[69] = _createTextVNode("? "))
                      ]),
                      _createElementVNode("div", _hoisted_27, [
                        _createElementVNode("div", _hoisted_28, [
                          _cache[70] || (_cache[70] = _createElementVNode("strong", null, "ID:", -1)),
                          _createTextVNode(" " + _toDisplayString(selectedDevice.value.id), 1)
                        ]),
                        _createElementVNode("div", _hoisted_29, [
                          _cache[71] || (_cache[71] = _createElementVNode("strong", null, "Ubicación:", -1)),
                          _createTextVNode(" " + _toDisplayString(getLocationById(selectedDevice.value.locationId)?.name), 1)
                        ]),
                        _createElementVNode("div", _hoisted_30, [
                          _cache[72] || (_cache[72] = _createElementVNode("strong", null, "Compañía:", -1)),
                          _createTextVNode(" " + _toDisplayString(getCompanyByLocationId(selectedDevice.value.locationId)?.name), 1)
                        ])
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_actions, { class: "px-6 pb-4" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_spacer),
                      _createVNode(_component_v_btn, {
                        variant: "text",
                        onClick: closeDialogs
                      }, {
                        default: _withCtx(() => _cache[73] || (_cache[73] = [
                          _createTextVNode(" Cancelar ")
                        ])),
                        _: 1
                      }),
                      _createVNode(_component_v_btn, {
                        color: "error",
                        onClick: deleteDevice
                      }, {
                        default: _withCtx(() => _cache[74] || (_cache[74] = [
                          _createTextVNode(" Eliminar Dispositivo ")
                        ])),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})